import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { apiClient } from '../../api/client';
import { isAtLeast } from '../../utils/roles';
import { TierSelector } from './TierSelector';
import { TierManagement } from './TierManagement';
import { RevenueByTier } from './RevenueByTier';
import { TieredRevenueInput } from './TieredRevenueInput';
import { ScheduleHighlightsManager } from './ScheduleHighlightsManager';
import { PRIMARY, TEXT_MUTED, BORDER_LIGHT, BG_PAGE } from './table-management/designTokens';

interface MealOption {
  id: string;
  name: string;
  description: string;
  active: boolean;
  icon: string | null;
  color: string | null;
}

interface TicketPriceData {
  id: string;
  amount: string;
}

interface RevenueData {
  ticketsSold: number;
  revenue: number;
  ticketCapacity: number;
  ticketsAvailable: number;
}

interface UnassignedGuest {
  guestId: string;
  name: string;
  chapterAffiliation: string | null;
  familyAssociation: string | null;
  accessibilityNeeds: string | null;
  protocolVip: boolean;
  referenceId: string | null;
}

export interface LegacyBallSettingsViewProps {
  user: { sub: string; role: string } | null;
}

type SettingsSubTab = 'pricing-revenue' | 'meal-options' | 'guest-list' | 'schedule';

interface TabDefinition {
  key: SettingsSubTab;
  label: string;
  organizerOnly?: boolean;
}

const SETTINGS_TABS: TabDefinition[] = [
  { key: 'pricing-revenue', label: 'Pricing & Revenue' },
  { key: 'meal-options', label: 'Meal Options' },
  { key: 'guest-list', label: 'Guest List' },
  { key: 'schedule', label: 'Schedule', organizerOnly: true },
];

const styles: Record<string, React.CSSProperties> = {
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnDanger: { padding: '0.4rem 0.75rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', marginRight: '0.5rem' },
  mealItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', borderBottom: '1px solid #eee' },
  summaryCard: { display: 'inline-block', padding: '1rem 1.5rem', backgroundColor: '#f5f5f5', borderRadius: '6px', marginRight: '1rem', marginBottom: '0.5rem' },
  summaryValue: { fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c' },
  summaryLabel: { fontSize: '0.8rem', color: '#666' },
  guestItem: { padding: '0.5rem 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  subNav: { display: 'flex', gap: '0', borderBottom: `1px solid ${BORDER_LIGHT}`, marginBottom: '1.5rem' },
  subTab: { padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 500, color: TEXT_MUTED, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', borderBottom: '2px solid transparent', marginBottom: '-1px', transition: 'color 0.2s, border-color 0.2s' },
  subTabActive: { padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600, color: PRIMARY, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', borderBottom: `2px solid ${PRIMARY}`, marginBottom: '-1px' },
};

export function LegacyBallSettingsView({ user }: LegacyBallSettingsViewProps) {
  const isOrganizer = isAtLeast(user?.role || '', 'Organizer');
  const [activeSubTab, setActiveSubTab] = useState<SettingsSubTab>('pricing-revenue');

  const [mealOptions, setMealOptions] = useState<MealOption[]>([]);
  const [ticketPrice, setTicketPrice] = useState<TicketPriceData | null>(null);
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [unassigned, setUnassigned] = useState<UnassignedGuest[]>([]);
  const [newMealName, setNewMealName] = useState('');
  const [newMealDesc, setNewMealDesc] = useState('');
  const [newMealIcon, setNewMealIcon] = useState('');
  const [newMealColor, setNewMealColor] = useState('');
  const [mealColorError, setMealColorError] = useState('');
  const [editingMealId, setEditingMealId] = useState<string | null>(null);
  const [newPrice, setNewPrice] = useState('');
  const [newCapacity, setNewCapacity] = useState('');
  const [tieredTicketsSold, setTieredTicketsSold] = useState(0);
  const [tieredRevenue, setTieredRevenue] = useState(0);
  const [sortMode, setSortMode] = useState<'none' | 'order' | 'chapter' | 'chapter-order'>('none');
  const [error, setError] = useState('');

  async function fetchAll() {
    try {
      const [meals, price, rev] = await Promise.all([
        apiClient<MealOption[]>('/legacy-ball/meal-options'),
        apiClient<TicketPriceData>('/legacy-ball/ticket-price').catch(() => null),
        apiClient<RevenueData>('/legacy-ball/revenue').catch(() => null),
      ]);
      setMealOptions(meals);
      setTicketPrice(price);
      setRevenue(rev);
    } catch {
      setError('Failed to load Legacy Ball data.');
    }
    try {
      const guests = await apiClient<UnassignedGuest[]>('/table-management/reports/unassigned');
      setUnassigned(guests);
    } catch {
      setUnassigned([]);
    }
  }

  useEffect(() => { fetchAll(); }, []);

  async function handleCreateMeal(e: FormEvent) {
    e.preventDefault();
    if (!newMealName.trim()) return;
    if (newMealColor && !/^#[0-9A-Fa-f]{6}$/.test(newMealColor)) {
      setMealColorError('Color must be a valid hex code (e.g., #c62828)');
      return;
    }
    setMealColorError('');
    try {
      const body: Record<string, unknown> = { name: newMealName, description: newMealDesc };
      if (newMealIcon) body.icon = newMealIcon;
      if (newMealColor) body.color = newMealColor;

      if (editingMealId) {
        await apiClient(`/legacy-ball/meal-options/${editingMealId}`, { method: 'PATCH', body });
        setEditingMealId(null);
      } else {
        await apiClient('/legacy-ball/meal-options', { method: 'POST', body });
      }
      setNewMealName('');
      setNewMealDesc('');
      setNewMealIcon('');
      setNewMealColor('');
      fetchAll();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save meal option.');
    }
  }

  function handleEditMeal(meal: MealOption) {
    setEditingMealId(meal.id);
    setNewMealName(meal.name);
    setNewMealDesc(meal.description);
    setNewMealIcon(meal.icon || '');
    setNewMealColor(meal.color || '');
    setMealColorError('');
  }

  function handleCancelEdit() {
    setEditingMealId(null);
    setNewMealName('');
    setNewMealDesc('');
    setNewMealIcon('');
    setNewMealColor('');
    setMealColorError('');
  }

  async function handleDeactivateMeal(id: string) {
    try {
      await apiClient(`/legacy-ball/meal-options/${id}`, { method: 'PATCH', body: { active: false } });
      fetchAll();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to deactivate meal option.');
    }
  }

  async function handleSetPrice(e: FormEvent) {
    e.preventDefault();
    if (!newPrice || parseFloat(newPrice) <= 0) return;
    try {
      await apiClient('/legacy-ball/ticket-price', { method: 'PUT', body: { amount: parseFloat(newPrice) } });
      setNewPrice('');
      fetchAll();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to set ticket price.');
    }
  }

  async function handleSetCapacity(e: FormEvent) {
    e.preventDefault();
    if (!newCapacity || parseInt(newCapacity) <= 0) return;
    try {
      await apiClient('/legacy-ball/ticket-capacity', { method: 'PUT', body: { capacity: parseInt(newCapacity) } });
      setNewCapacity('');
      fetchAll();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to set ticket capacity.');
    }
  }

  const handleTotalsChange = useCallback((totalTickets: number, totalRevenue: number) => {
    setTieredTicketsSold(totalTickets);
    setTieredRevenue(totalRevenue);
  }, []);

  const visibleTabs = SETTINGS_TABS.filter(tab => !tab.organizerOnly || isOrganizer);

  return (
    <div style={{ backgroundColor: BG_PAGE, padding: '0.5rem 0' }}>
      {error && <div style={styles.error}>{error}</div>}

      {/* Sub-Tab Navigation Bar */}
      <nav style={styles.subNav} aria-label="Settings views">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            style={activeSubTab === tab.key ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveSubTab(tab.key)}
            aria-current={activeSubTab === tab.key ? 'page' : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Pricing & Revenue Tab */}
      {activeSubTab === 'pricing-revenue' && (
        <>
          {/* Ticket Price */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Ticket Price</h2>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
              Current Price: <strong style={{ color: '#1a6b3c' }}>{ticketPrice ? `$${parseFloat(ticketPrice.amount).toFixed(2)}` : 'Not set'}</strong>
            </p>
            <form onSubmit={handleSetPrice} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="number" step="0.01" min="0.01" style={styles.input} placeholder="New price" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
              <button type="submit" style={styles.btn}>Set Price</button>
            </form>
            <div style={{ marginTop: '1.5rem' }}>
              <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                Ticket Capacity: <strong style={{ color: '#1a6b3c' }}>{revenue?.ticketCapacity ?? 'Not set'}</strong>
              </p>
              <form onSubmit={handleSetCapacity} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="number" min="1" style={styles.input} placeholder="Max tickets" value={newCapacity} onChange={(e) => setNewCapacity(e.target.value)} />
                <button type="submit" style={styles.btn}>Set Capacity</button>
              </form>
            </div>
          </div>

          {/* Ticket Tier Selection */}
          <div style={styles.section}>
            <TierSelector registrationId={user?.sub || ''} onPurchaseSuccess={fetchAll} />
          </div>

          {/* Organizer-only: Tier Management */}
          {isOrganizer && <TierManagement />}

          {/* Organizer-only: Revenue by Tier */}
          {isOrganizer && <RevenueByTier />}

          {/* Revenue Summary */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Revenue Summary</h2>
            <div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{revenue?.ticketCapacity ?? 0}</div>
                <div style={styles.summaryLabel}>Ticket Capacity</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{tieredTicketsSold}</div>
                <div style={styles.summaryLabel}>Tickets Sold</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{(revenue?.ticketCapacity ?? 0) - tieredTicketsSold}</div>
                <div style={styles.summaryLabel}>Tickets Available</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>${tieredRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                <div style={styles.summaryLabel}>Total Revenue</div>
              </div>
            </div>
            {isOrganizer && (
              <div style={{ marginTop: '1.5rem' }}>
                <TieredRevenueInput onTotalsChange={handleTotalsChange} />
              </div>
            )}
          </div>
        </>
      )}

      {/* Meal Options Tab */}
      {activeSubTab === 'meal-options' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Meal Options</h2>
          {mealOptions.map((meal) => (
            <div key={meal.id} style={styles.mealItem}>
              <div>
                <strong>
                  {meal.icon && <span style={{ marginRight: '0.4rem' }}>{meal.icon}</span>}
                  {meal.name}
                </strong>
                {!meal.active && <span style={{ color: '#999', marginLeft: '0.5rem' }}>(Inactive)</span>}
                {meal.color && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      backgroundColor: meal.color,
                      borderRadius: '3px',
                      marginLeft: '0.5rem',
                      verticalAlign: 'middle',
                      border: '1px solid #ccc',
                    }}
                    title={meal.color}
                  />
                )}
                <div style={{ fontSize: '0.85rem', color: '#666' }}>{meal.description}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1 }}
                  onClick={() => handleEditMeal(meal)}
                  title="Edit"
                  aria-label={`Edit ${meal.name}`}
                >✏️</button>
                {meal.active && (
                  <button style={styles.btnDanger} onClick={() => handleDeactivateMeal(meal.id)}>Deactivate</button>
                )}
              </div>
            </div>
          ))}
          <form onSubmit={handleCreateMeal} style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input style={styles.input} placeholder="Meal name" value={newMealName} onChange={(e) => setNewMealName(e.target.value)} />
              <input style={styles.input} placeholder="Description" value={newMealDesc} onChange={(e) => setNewMealDesc(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                style={{ ...styles.input, width: '60px' }}
                placeholder="🥩"
                value={newMealIcon}
                onChange={(e) => setNewMealIcon(e.target.value)}
                title="Icon (emoji)"
                aria-label="Meal icon emoji"
              />
              <input
                type="color"
                value={newMealColor || '#1a6b3c'}
                onChange={(e) => { setNewMealColor(e.target.value); setMealColorError(''); }}
                style={{ width: '36px', height: '36px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                title="Pick color"
                aria-label="Meal color picker"
              />
              <input
                style={{ ...styles.input, width: '100px' }}
                placeholder="#c62828"
                value={newMealColor}
                onChange={(e) => { setNewMealColor(e.target.value); setMealColorError(''); }}
                title="Hex color code"
                aria-label="Meal hex color code"
              />
              {newMealColor && (
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#666' }}
                  onClick={() => setNewMealColor('')}
                  title="Clear color"
                  aria-label="Clear color"
                >✕</button>
              )}
            </div>
            {mealColorError && <div style={{ color: '#d32f2f', fontSize: '0.8rem' }}>{mealColorError}</div>}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" style={styles.btn}>{editingMealId ? 'Update Meal' : 'Add Meal'}</button>
              {editingMealId && (
                <button type="button" style={{ ...styles.btn, backgroundColor: '#6b7280' }} onClick={handleCancelEdit}>Cancel</button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Guest List Tab */}
      {activeSubTab === 'guest-list' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Unassigned Guests ({unassigned.length})</h2>
          {unassigned.length === 0 ? (
            <p style={{ color: '#666' }}>All guests have been assigned seats.</p>
          ) : (
            <>
              <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  style={{ ...styles.btn, backgroundColor: sortMode === 'order' ? '#c9a84c' : '#1a6b3c' }}
                  onClick={() => setSortMode(sortMode === 'order' ? 'none' : 'order')}
                >
                  {sortMode === 'order' ? '✓ ' : ''}Order ID
                </button>
                <button
                  style={{ ...styles.btn, backgroundColor: sortMode === 'chapter' ? '#c9a84c' : '#1a6b3c' }}
                  onClick={() => setSortMode(sortMode === 'chapter' ? 'none' : 'chapter')}
                >
                  {sortMode === 'chapter' ? '✓ ' : ''}Chapter
                </button>
                <button
                  style={{ ...styles.btn, backgroundColor: sortMode === 'chapter-order' ? '#c9a84c' : '#1a6b3c' }}
                  onClick={() => setSortMode(sortMode === 'chapter-order' ? 'none' : 'chapter-order')}
                >
                  {sortMode === 'chapter-order' ? '✓ ' : ''}Chapter + Order
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: '#333' }}>Name</th>
                      <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: '#333' }}>Chapter</th>
                      <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: '#333' }}>Order ID</th>
                      <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: '#333' }}>Accessibility</th>
                      <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: '#333' }}>VIP</th>
                      <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: '#333' }}>Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...unassigned]
                      .sort((a, b) => {
                        if (sortMode === 'none') return 0;
                        if (sortMode === 'order') {
                          if (!a.referenceId && !b.referenceId) return 0;
                          if (!a.referenceId) return 1;
                          if (!b.referenceId) return -1;
                          return a.referenceId.localeCompare(b.referenceId);
                        }
                        if (sortMode === 'chapter') {
                          const chA = a.chapterAffiliation || 'zzz';
                          const chB = b.chapterAffiliation || 'zzz';
                          return chA.localeCompare(chB);
                        }
                        if (sortMode === 'chapter-order') {
                          const chA = a.chapterAffiliation || 'zzz';
                          const chB = b.chapterAffiliation || 'zzz';
                          const chCompare = chA.localeCompare(chB);
                          if (chCompare !== 0) return chCompare;
                          if (!a.referenceId && !b.referenceId) return 0;
                          if (!a.referenceId) return 1;
                          if (!b.referenceId) return -1;
                          return a.referenceId.localeCompare(b.referenceId);
                        }
                        return 0;
                      })
                      .map((guest) => (
                        <tr key={guest.guestId} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{guest.name}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#666' }}>{guest.chapterAffiliation || '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>{guest.referenceId || '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>
                            {guest.accessibilityNeeds && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1565c0',
                                  padding: '0.15rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                                title={guest.accessibilityNeeds}
                              >
                                ♿
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>
                            {guest.protocolVip && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  backgroundColor: '#fff8e1',
                                  color: '#f9a825',
                                  padding: '0.15rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                }}
                              >
                                VIP
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#666', fontSize: '0.8rem' }}>
                            {guest.familyAssociation || '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Schedule Tab (Organizer only) */}
      {activeSubTab === 'schedule' && isOrganizer && <ScheduleHighlightsManager />}
    </div>
  );
}
